import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { layersQuery } from '@/queries/layer.query';
import { groundClassifyTrackingAtom } from '@/atoms/groundClassify-progress.atom';

/**
 * Mounts once per project shell (alongside `useDeviationProgress`).
 *
 * Same shape as the deviation tracker hook, with two simplifications:
 *
 * 1. **No per-job query.** Deviation has a dedicated `deviationJobs`
 *    GraphQL query that fuels both the toast lifecycle AND a navbar
 *    badge. Ground classification doesn't have a badge — the only thing
 *    we need is success/failure detection per active tracker, which we
 *    get from the generic `jobs(projectId)` list filtered to GROUND_CLASSIFY.
 *
 * 2. **Single layer match resolves the toast.** A run produces TWO derived
 *    layers (POINTCLOUD from the LAS child, TILE from the GEOTIFF child),
 *    but they appear at the same time because postprocess inserts them
 *    in one transaction. We resolve as soon as either arrives.
 *
 * Polling cadence: explicit `setInterval(..., 2000)` driven by
 * `hasTrackers`, calling `refetch()` on both queries directly. Same
 * rationale as `useDeviationProgress`: shared query keys with page-level
 * subscribers make `refetchInterval` flaky.
 */
export function useGroundClassifyProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(groundClassifyTrackingAtom);
  const queryClient = useQueryClient();

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  // Explicit polling. While we have local trackers, poll both queries every
  // 2 s. `refetch()` bypasses `enabled` and observer-coordination issues
  // that bit the deviation hook in earlier iterations.
  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    const tick = () => {
      void refetchJobs();
      void refetchLayers();
    };
    // Fire once immediately so the user doesn't wait a full 2 s after
    // clicking Run before any progress check happens.
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;

    const jobs = jobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // Failure path: GROUND_CLASSIFY parent job (or any of its child
        // leaves) hit FAILED. Match by batchId and resolve the toast as
        // an error so the user isn't left staring at an infinite spinner.
        const jobInBatch = jobs.find((j) => j.batchId === tracker.batchId);
        if (jobInBatch?.status === 'FAILED') {
          toast.error(
            t('groundClassification.failedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 5000 },
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }

        // Success path: a layer with `metadata.derivedFrom === sourceLayerId`
        // that wasn't in the pre-run snapshot has appeared. postprocess
        // sets `derivedFrom` on both the LAS-leaf POINTCLOUD layer and
        // the GEOTIFF-leaf TILE layer (see server/background/src/functions/
        // postprocess.ts). Either is sufficient evidence the run finished.
        const existingIds = new Set(tracker.existingDerivedLayerIds);
        const newLayer = layers.find((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          if (meta.derivedFrom !== tracker.sourceLayerId) return false;
          return !existingIds.has(l.id);
        });
        if (newLayer) {
          toast.success(
            t('groundClassification.completedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 5000 },
          );
          queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, layersData, hasTrackers, projectId, queryClient, setTrackers, t]);
}
